// D-1012 (ひろ 2026-10-08 承認): 残っていた字の矢印(‹ › ▲ ▼)を線のアイコンにそろえる。
//   A. 字の付いた戻る(「← 戻る」など)→ ヘッダは NavIcons の BackIcon(48×48)、行き先を言う戻るは LabeledBackIcon + 字
//   B. 月送り・日送り(‹ 前月 / 次月 ›)→ ChevronLeftIcon / ChevronRightIcon
//   C. 行の右端の ›            → RowChevron(1 つの大きさ・1 つの濃さ)
//      開け閉めの ▲/▼          → DisclosureChevron(chevron-down。開くと 180° 回る)
//      字のリンクの後ろの ›    → LinkChevron(「一覧 ›」など。字の色のまま)
//      表の並べ替えの ▲/▼/⇅   → SortArrow(arrow-up / arrow-down / arrow-up-down)
// 出典: Lucide (https://lucide.dev) v0.460 — ISC License (Copyright (c) Lucide Contributors)。
//   chevron-left / chevron-right / chevron-down / arrow-up / arrow-down / arrow-up-down。
//   ★NavIcons.jsx と同じ流儀: lucide-react は入れず、パスをリテラルで持つ。viewBox 24 / stroke 2 / round / currentColor。
//   ★D-1012 の時点で NavIcons の NavSvg は export されていなかったので、同じ形の wrapper をここに持つ(D-1011 で NavSvg は export 済み。寄せるのは後続で)。
//   ★shared は shared しか import できない(eslint-plugin-boundaries)。
import React from 'react'
import { BackIcon } from './NavIcons'

/** 月送り・日送りのアイコンの一辺(px)。44px 以上の箱の中で 22px。 */
export const STEPPER_ICON_PX = 22
/** 行の右端の › の一辺(px)。全画面で同じ。 */
export const ROW_CHEVRON_PX = 20
/** 開け閉めの印の一辺(px)。 */
export const DISCLOSURE_ICON_PX = 18
/** 並べ替えの矢印の一辺(px)。字(text-xs)と同じくらい。 */
export const SORT_ICON_PX = 12

function ChevronSvg({ name, size, className, style, children }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      // 指は常にボタン本体に当たる(e.target / elementFromPoint が svg・path にならない)
      style={{ pointerEvents: 'none', ...style }}
      data-icon={name}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** lucide: chevron-left */
export function ChevronLeftIcon({ size = STEPPER_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="chevron-left" size={size} {...rest}>
      <path d="m15 18-6-6 6-6" />
    </ChevronSvg>
  )
}

/** lucide: chevron-right */
export function ChevronRightIcon({ size = STEPPER_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="chevron-right" size={size} {...rest}>
      <path d="m9 18 6-6-6-6" />
    </ChevronSvg>
  )
}

/** lucide: chevron-up(D-1011: 報告種別マスタの並び替え「上へ」) */
export function ChevronUpIcon({ size = DISCLOSURE_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="chevron-up" size={size} {...rest}>
      <path d="m18 15-6-6-6 6" />
    </ChevronSvg>
  )
}

/** lucide: chevron-down */
export function ChevronDownIcon({ size = DISCLOSURE_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="chevron-down" size={size} {...rest}>
      <path d="m6 9 6 6 6-6" />
    </ChevronSvg>
  )
}

/** lucide: arrow-up */
export function ArrowUpIcon({ size = SORT_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="arrow-up" size={size} {...rest}>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </ChevronSvg>
  )
}

/** lucide: arrow-down */
export function ArrowDownIcon({ size = SORT_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="arrow-down" size={size} {...rest}>
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </ChevronSvg>
  )
}

/** lucide: arrow-up-down(並べ替えていない列の印) */
export function ArrowUpDownIcon({ size = SORT_ICON_PX, ...rest }) {
  return (
    <ChevronSvg name="arrow-up-down" size={size} {...rest}>
      <path d="m21 16-4 4-4-4" />
      <path d="M17 20V4" />
      <path d="m3 8 4-4 4 4" />
      <path d="M7 4v16" />
    </ChevronSvg>
  )
}

/** 字の付いた戻る(「ハブに戻る」「検索に戻る」など行き先を言う戻る)の頭に付けるアイコンの一辺(px)。 */
export const LABELED_BACK_ICON_PX = 20
/**
 * 字の付いた戻るの頭の矢印。中身は戻る(48×48)と同じ arrow-big-left で、字の大きさに合わせて 20px。
 *   ★ボタンは inline-flex items-center gap-1.5 で字と並べる(高さ 44px 以上はボタン側で持つ)。
 */
export function LabeledBackIcon() {
  return <BackIcon size={LABELED_BACK_ICON_PX} className="shrink-0" />
}

/** 行の右端の印。大きさ・色・濃さは全画面で 1 つ(差し替えるときはここだけ直す)。
 *  @param {string} layout 置き場所だけの class(ml-auto など)。大きさ・色・濃さは渡さない。 */
export const ROW_CHEVRON_CLASS = 'shrink-0 text-muted opacity-60'
export function RowChevron({ layout = '' }) {
  return <ChevronRightIcon size={ROW_CHEVRON_PX} className={`${ROW_CHEVRON_CLASS} ${layout}`.trim()} />
}

/** 字のリンク(「一覧 ›」「内訳 ›」など)の後ろの印の一辺(px)。色は字と同じ(currentColor)。 */
export const LINK_CHEVRON_PX = 14
export function LinkChevron() {
  return <ChevronRightIcon size={LINK_CHEVRON_PX} className="inline-block shrink-0 align-[-2px]" />
}

/**
 * 開け閉めの印。閉じているとき下向き、開いているとき 180° 回って上向き。
 * @param {boolean} open 開いているか
 * @param {string} className 色など(既定 text-muted)
 */
export function DisclosureChevron({ open = false, size = DISCLOSURE_ICON_PX, className = 'text-muted' }) {
  return (
    <ChevronDownIcon
      size={size}
      className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
    />
  )
}

/** 並べ替えの矢印。asc = 上向き、desc = 下向き、それ以外(並べ替えていない列)= 上下。 */
export function SortArrow({ dir, size = SORT_ICON_PX, className }) {
  if (dir === 'asc') return <ArrowUpIcon size={size} className={className} />
  if (dir === 'desc') return <ArrowDownIcon size={size} className={className} />
  return <ArrowUpDownIcon size={size} className={className} />
}
